import {
	Button,
	Pagination,
	TextField,
	Tooltip,
} from '@mui/material';
import {
	Refresh,
	UploadFile,
} from '@mui/icons-material';
import { CheckCircle2, Save } from 'lucide-react';
import { SelectPicker } from 'rsuite';

import { BackdropLoader } from '@/Components/Loaders';
import { BaseModal } from '@/Components/Modals/BaseModal';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import TrainingStatusModal from './TrainingStatusModal';

const getModalHeading = (prefix, item) => {
	return `${prefix}${item?.shapeName ? ` - ${item.shapeName}` : ''}`;
};

const ASSIGNEE_PICKER_CLASS =
	'min-w-[16rem] [&_.rs-picker-toggle]:!h-9 [&_.rs-picker-toggle]:!rounded-md [&_.rs-picker-toggle]:!border [&_.rs-picker-toggle]:!border-slate-300 [&_.rs-picker-toggle]:!bg-white [&_.rs-picker-toggle]:!px-2.5 [&_.rs-picker-toggle]:!text-left [&_.rs-picker-toggle]:!text-sm [&_.rs-picker-toggle:hover]:!border-slate-500';

const ShapeModelLibraryModals = ({
	activeModalKey,
	shapeModal,
	shapeForm,
	shapeFormErrors,
	setShapeForm,
	setShapeFormErrors,
	loading,
	closeShapeModal,
	handleShapeSubmit,
	productModal,
	productLoading,
	productSearch,
	setProductSearch,
	selectedProductIds,
	productRows,
	productsTotalCount,
	productPage,
	totalProductPages,
	goToProductPage,
	productTableRows,
	productColMapping,
	productColumns,
	renderTableHeader,
	closeProductModal,
	saveProductAssignments,
	taskModal,
	taskLoading,
	taskForm,
	setTaskForm,
	closeTaskModal,
	handleCreateTask,
	canAssign,
	consoleUserPickerData,
	jobsModal,
	jobsLoading,
	assignment,
	assignmentLoading,
	assignmentUnavailable,
	handleUnassignTask,
	handleCompleteTask,
	pendingJobUpdateCount,
	fetchCvatJobs,
	saveJobUpdates,
	jobTableRows,
	jobColMapping,
	jobColumns,
	closeJobsModal,
	readinessModal,
	readinessLoading,
	prepareLoading,
	trainLoading,
	canPrepareTrainingData,
	canStartTraining,
	closeReadinessModal,
	handlePrepareTrainingData,
	refreshTrainingStatus,
	handleStartTraining,
	uploadModal,
	uploadLoading,
	modelVersion,
	setModelVersion,
	uploadFile,
	setUploadFile,
	closeUploadModal,
	handleUploadSubmit,
}) => {
	if (!activeModalKey) return null;

	if (activeModalKey === 'readiness') {
		return (
			<TrainingStatusModal
				open={readinessModal.open}
				item={readinessModal.item}
				data={readinessModal.data}
				readinessLoading={readinessLoading}
				prepareLoading={prepareLoading}
				trainLoading={trainLoading}
				canPrepareTrainingData={canPrepareTrainingData}
				canStartTraining={canStartTraining}
				onClose={closeReadinessModal}
				onPrepare={handlePrepareTrainingData}
				onRefresh={refreshTrainingStatus}
				onStartTraining={handleStartTraining}
			/>
		);
	}

	let modalProps = null;
	let content = null;

	if (activeModalKey === 'shape') {
		modalProps = {
			open: shapeModal.open,
			heading:
				shapeModal.mode === 'UPDATE'
					? 'Edit Shape Model'
					: 'Create Shape Model',
			width: '34rem',
			height: '18rem',
			handleClose: closeShapeModal,
			handleConfirm: handleShapeSubmit,
		};
		content = (
			<div className='relative flex h-full flex-col items-stretch gap-5 pt-3'>
				<BackdropLoader isLoading={loading} showProgress />
				<TextField
					fullWidth
					required
					label='Shape Name'
					value={shapeForm.shapeName}
					error={Boolean(shapeFormErrors.shapeName)}
					helperText={shapeFormErrors.shapeName}
					onChange={(e) => {
						setShapeForm({
							...shapeForm,
							shapeName: e.target.value,
						});
						setShapeFormErrors({});
					}}
				/>
			</div>
		);
	}

	if (activeModalKey === 'product') {
		modalProps = {
			open: productModal.open,
			heading: getModalHeading('Assign Products', productModal?.item),
			width: '62rem',
			height: '42rem',
			handleClose: closeProductModal,
			handleConfirm: saveProductAssignments,
		};
		content = (
			<div className='relative flex h-full flex-col items-stretch gap-3 overflow-hidden'>
				<BackdropLoader isLoading={productLoading} showProgress />
				<div className='flex items-center justify-between gap-4'>
					<TextField
						size='small'
						value={productSearch}
						placeholder='Search products'
						onChange={(e) => setProductSearch(e.target.value)}
					/>
					<div className='whitespace-nowrap rounded-md border border-teal-200 bg-teal-50 px-3 py-2 font-semibold text-teal-700'>
						{selectedProductIds.length}
						{' selected'}
					</div>
				</div>
				<div className='min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200'>
					<VariantTable
						colMapping={productColMapping}
						columns={productColumns}
						data={productRows}
						customRowData={productTableRows}
						customCol={renderTableHeader}
						customInfoLine='No products found'
						virtualization={productRows.length > 100}
					/>
				</div>
				<div className='flex items-center justify-between gap-3 text-xs text-slate-500'>
					<span>
						{`Page ${productPage} of ${totalProductPages} · ${productsTotalCount} assignable`}
					</span>
					<Pagination
						size='small'
						color='primary'
						shape='rounded'
						count={totalProductPages}
						page={productPage}
						disabled={productLoading || totalProductPages <= 1}
						onChange={(_, nextPage) => goToProductPage(nextPage)}
					/>
				</div>
			</div>
		);
	}

	if (activeModalKey === 'task') {
		modalProps = {
			open: taskModal.open,
			heading: getModalHeading('Create CVAT Task', taskModal?.item),
			width: '36rem',
			height: canAssign ? '28rem' : '22rem',
			handleClose: closeTaskModal,
			handleConfirm: handleCreateTask,
		};
		content = (
			<div className='relative flex h-full flex-col items-stretch gap-4 pt-3'>
				<BackdropLoader isLoading={taskLoading} showProgress />
				<div className='rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm font-semibold text-yellow-800'>
					Queued images:
					{' '}
					{taskModal?.item?.pendingSelectionCount ?? 0}
				</div>
				<TextField
					fullWidth
					required
					type='number'
					label='Minimum Selection Count'
					value={taskForm.minSelectionCount}
					inputProps={{
						min: 1,
						step: 1,
					}}
					onChange={(e) => {
						setTaskForm({
							...taskForm,
							minSelectionCount: e.target.value,
						});
					}}
				/>
				{canAssign && (
					<div className='flex flex-col gap-1'>
						<span className='text-sm font-semibold text-slate-700'>
							Assign to
						</span>
						<SelectPicker
							block
							className={ASSIGNEE_PICKER_CLASS}
							data={consoleUserPickerData}
							value={taskForm.assigneeId}
							placeholder='Assign to me'
							menuMaxHeight={220}
							menuClassName='z-[99999]'
							placement='bottomStart'
							container={() => document.body}
							onChange={(value) => {
								setTaskForm({
									...taskForm,
									assigneeId: value,
								});
							}}
						/>
						<span className='text-xs text-slate-500'>
							Leave empty to keep the task assigned to yourself.
						</span>
					</div>
				)}
			</div>
		);
	}

	if (activeModalKey === 'jobs') {
		// assignedBy comes back as a bare user id; resolve it against the same
		// user list that feeds the picker.
		const assignedByLabel = consoleUserPickerData?.find((option) => {
			return option.value === assignment?.assignedBy;
		})?.label;
		modalProps = {
			open: jobsModal.open,
			heading: getModalHeading('CVAT Jobs', jobsModal?.item),
			width: '76rem',
			height: '42rem',
			handleClose: closeJobsModal,
			hideFooter: true,
		};
		content = (
			<div className='relative flex h-full flex-col items-stretch gap-3 overflow-hidden'>
				<BackdropLoader isLoading={jobsLoading} showProgress />
				<div className='flex items-center justify-between gap-3'>
					<div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700'>
						Task ID:
						{' '}
						{jobsModal?.item?.cvatTaskId ?? jobsModal?.item?.taskId ?? '-'}
					</div>
					<div className='flex flex-wrap items-center justify-end gap-2'>
						<Button
							className='!rounded-md !bg-black !px-4 !py-2 !font-medium !normal-case !text-white hover:!bg-slate-800'
							startIcon={<Refresh />}
							disabled={jobsLoading}
							onClick={() => fetchCvatJobs(jobsModal.item)}
						>
							Refresh Jobs
						</Button>
						<Button
							className='!rounded-md !bg-uy-color !px-4 !py-2 !font-medium !normal-case !text-black hover:!bg-yellow-500 disabled:!bg-slate-200 disabled:!text-slate-500'
							startIcon={<Save size={18} />}
							disabled={jobsLoading || pendingJobUpdateCount === 0}
							onClick={saveJobUpdates}
						>
							Save Changes
						</Button>
					</div>
				</div>
				{/*
				  The assignee picker lives on the job row. Completion stays here:
				  it is a task-level action and writes the payable counts.
				*/}
				<div className='flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-slate-200 bg-white px-3 py-2'>
					{/* eslint-disable-next-line no-nested-ternary */}
					{assignmentUnavailable ? (
						<span className='rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800'>
							Could not load this task&apos;s assignment — controls are
							locked. Use Refresh Jobs to retry.
						</span>
					) : assignment?.assignedAt ? (
						<span className='text-xs text-slate-500'>
							Assigned
							{' '}
							{assignment.assignedAt}
							{assignedByLabel ? ` by ${assignedByLabel}` : ''}
						</span>
					) : (
						<span className='text-xs text-slate-500'>
							Not assigned yet — pick an annotator in the Assignee column.
						</span>
					)}
					{canAssign && assignment && !assignment?.completedAt && (
						<Button
							size='small'
							className='!rounded-md !border !border-slate-300 !px-3 !py-1 !text-sm !font-medium !normal-case !text-slate-700 hover:!bg-slate-100'
							disabled={
								assignmentLoading || jobsLoading || assignmentUnavailable
							}
							onClick={handleUnassignTask}
						>
							Unassign
						</Button>
					)}
					<div className='ml-auto flex items-center gap-3'>
						{assignment?.completedAt ? (
							<span className='rounded-md border border-green-200 bg-green-50 px-3 py-1.5 text-sm font-semibold text-green-700'>
								Completed
								{' '}
								{assignment.completedAt}
								{' · '}
								{assignment.imagesAnnotated ?? 0}
								{' images annotated'}
							</span>
						) : (
							canAssign && (
								<Tooltip
									title={
										assignment
											? 'Writes the payable annotation counts'
											: 'Assign the task before marking it complete'
									}
								>
									<span>
										<Button
											className='!rounded-md !bg-green-600 !px-4 !py-2 !font-medium !normal-case !text-white hover:!bg-green-700 disabled:!bg-slate-200 disabled:!text-slate-500'
											startIcon={<CheckCircle2 size={18} />}
											disabled={
												!assignment ||
												assignmentLoading ||
												jobsLoading ||
												assignmentUnavailable
											}
											onClick={handleCompleteTask}
										>
											Mark complete
										</Button>
									</span>
								</Tooltip>
							)
						)}
					</div>
				</div>
				<div className='min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200'>
					<VariantTable
						colMapping={jobColMapping}
						columns={jobColumns}
						data={jobsModal.jobs}
						customRowData={jobTableRows}
						customCol={renderTableHeader}
						customInfoLine='No CVAT jobs found'
					/>
				</div>
			</div>
		);
	}

	if (activeModalKey === 'upload') {
		modalProps = {
			open: uploadModal.open,
			heading: getModalHeading('Upload Model Zip', uploadModal?.item),
			width: '36rem',
			height: '24rem',
			handleClose: closeUploadModal,
			handleConfirm: handleUploadSubmit,
		};
		content = (
			<div className='relative flex h-full flex-col items-stretch gap-5 pt-3'>
				<BackdropLoader isLoading={uploadLoading} showProgress />
				<TextField
					fullWidth
					type='number'
					label='Model Version (optional)'
					value={modelVersion}
					inputProps={{
						min: 1,
						step: 1,
					}}
					onChange={(e) => setModelVersion(e.target.value)}
				/>
				<div className='flex items-center gap-4'>
					<Button
						component='label'
						className='!rounded-md !bg-uy-color !px-4 !py-2 !font-medium !normal-case !text-black hover:!bg-yellow-500'
						startIcon={<UploadFile />}
					>
						Choose ZIP
						<input
							hidden
							type='file'
							accept='.zip,application/zip,application/x-zip-compressed'
							onChange={(e) => {
								setUploadFile(e.target.files?.[0] ?? null);
							}}
						/>
					</Button>
					<span className='overflow-hidden text-ellipsis whitespace-nowrap text-slate-600'>
						{uploadFile?.name ?? 'No file selected'}
					</span>
				</div>
			</div>
		);
	}

	if (!modalProps) return null;

	return (
		<BaseModal {...modalProps}>
			{content}
		</BaseModal>
	);
};

export default ShapeModelLibraryModals;
